import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import Check from '@mui/icons-material/Check';
import Clear from '@mui/icons-material/Clear';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';

import CloseButton from '../ui/CloseButton.tsx';
import PaperComponent from '../PaperComponent';

import api from '../../utils/api.ts';
import {
  setAlertsSnackbarOpen,
  setAlertsSnackbarSeverity,
  setAlertsSnackbarText,
} from '../../features/app/alertsSnackbarSlice';
import { removeFriendByUuid } from '../../features/network/networkSlice';
import { updateUserByUuid } from '../../features/users/usersSlice';

import { borderedDialogPaperProps } from '../../theme';
import { getUsers } from '../../utils/getters';

import { setConfirmRemoveFriendDialogOpen } from '../../features/dialogs/confirmRemoveFriendDialogSlice';

export default function ConfirmRemoveFriendDialog() {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState(false);

  const userPosition = useSelector((state) => state.userPosition.value);
  const withinMeters = useSelector((state) => state.settings.withinMeters);

  const open = useSelector((state) => state.confirmRemoveFriendDialog.open);
  const user = useSelector((state) => state.confirmRemoveFriendDialog.user);

  const handleClose = () => {
    dispatch(setConfirmRemoveFriendDialogOpen(false));
    setDeleteButtonDisabled(false);
  };

  const handleRemoveClick = async () => {
    if (deleteButtonDisabled) { return };
    setDeleteButtonDisabled(true);
    await api.delete('/users/friends/',
      { 'data': { uuid: user.properties.uuid } }).then(response => {
        dispatch(removeFriendByUuid(user.properties.uuid));
        dispatch(updateUserByUuid(user.properties.uuid, { friend: false }));
        if (userPosition.length === 2) {
          getUsers(dispatch, userPosition, withinMeters);
        };

        handleClose();

        dispatch(setAlertsSnackbarSeverity('info'));
        dispatch(setAlertsSnackbarText(t('Friend removed')));
        dispatch(setAlertsSnackbarOpen(true));
      }).catch(err => {
        dispatch(setAlertsSnackbarSeverity('error'));
        dispatch(setAlertsSnackbarText(t('Something went wrong.')));
        dispatch(setAlertsSnackbarOpen(true));

        setTimeout(() => {
          setDeleteButtonDisabled(false);
        }, 1500);
      });
  };

  return (
    <Dialog
      open={open}
      PaperComponent={PaperComponent}
      hideBackdrop={false}
      aria-labelledby="draggable-dialog-title"
      aria-describedby="alert-dialog-description"
      {...borderedDialogPaperProps}
    >
      <DialogTitle textAlign="center" style={{ cursor: 'move' }} id="draggable-dialog-title">
        {t('Confirm remove')}
      </DialogTitle>
      <CloseButton onClick={handleClose} />
      <form>
        <DialogContent>
          <Typography>
          </Typography>
          <DialogContentText id="alert-dialog-description">
            {t('Are you sure you want to remove')} <strong>{
              user.properties.username || user.properties.uuid
            }</strong> {t('from your friend list')}?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<Clear />}
            onClick={handleClose}>
            {t('Cancel')}
          </Button>
          <Button
            disabled={deleteButtonDisabled}
            onClick={handleRemoveClick}
            variant="outlined"
            endIcon={<Check />}
            color="error">
            {t('Remove')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
};